import { withStyles, TextField, Icon, Box, PartnerLogin, BodyText } from 'vopay';
import { useState, useEffect } from 'react';
import { Formik } from 'formik';
import styles from './Login.module.scss';
import TwoFactorAuthenticationContainer from './TwoFactorAuthentication/TwoFactorAuthenticationContainer';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import { useBranding } from '@/providers/Branding.provider';
import MSEntraButton, { type LoginResult } from './MSEntraButton/MsEntraButton';
import GoogleButton, { type GoogleLoginResult } from './GoogleButton/GoogleButton';
import ResetPasswordRequest from './ResetPasswordRequest/ResetPasswordRequest';
import { Title, Button, UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

const LoginForm = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'Login' });
  const { isSubmitting, login, response, storeLogin } = usePartnerLogin();
  const { ssoProviders } = useBranding();
  const [usingMsEntra, setUsingMsEntra] = useState(false);
  const [isLoggingInWithSso, setIsLoggingInWithSso] = useState(false);

  // A provider missing from the branding response means "enabled" (older backends keep working)
  const showMicrosoftSso = ssoProviders.MicrosoftEntra !== false;
  const showGoogleSso = ssoProviders.Google !== false && Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
  const [shouldShowLoginPage, setShouldShowLoginPage] = useState(true);
  const [shouldShow2FA, setShouldShow2FA] = useState(false);
  const [shouldResetPassword, setShouldResetPassword] = useState(false);

  const [loginRequest, setLoginRequest] = useState({
    Username: '',
    Password: '',
  });

  const handlePartnerLogin = (values: PartnerLogin) => {
    setIsLoggingInWithSso(false);

    setLoginRequest({
      Username: values.username || '',
      Password: values.password || '',
    });

    login({
      username: values.username || '',
      password: values.password || '',
    });
  };

  const TwoFAEnabled = import.meta.env.VITE_2FA_Enabled === 'true';

  useEffect(() => {
    if (response.success !== undefined) {
      // SSO logins skip local 2FA: the identity provider already performed MFA, and the
      // account's local password is random/unused, so there is nothing to step up here.
      // MFAEnabled can't carry this on its own — for password users `false` means
      // "not enrolled yet", which is exactly when we DO want the 2FA screen.
      if (
        !isLoggingInWithSso &&
        (response.MFAEnabled === false || localStorage.getItem('rememberDevice') !== 'true') &&
        TwoFAEnabled === true
      ) {
        setShouldShowLoginPage(false);
        setShouldShow2FA(true);
      } else {
        storeLogin();
      }
    }
  }, [response]);

  const Continue2Fa = () => {
    storeLogin();
  };

  const Cancel2Fa = () => {
    setShouldShow2FA(false);
    setShouldShowLoginPage(true);
  };

  function handleMsLogin({ email, accessToken }: LoginResult) {
    setIsLoggingInWithSso(true);

    login({
      username: email,
      SSOType: 'MicrosoftEntra',
      SSOToken: accessToken,
    });
  }

  function handleGoogleLogin({ email, credential }: GoogleLoginResult) {
    setIsLoggingInWithSso(true);

    login({
      username: email,
      SSOType: 'Google',
      SSOToken: credential,
    });
  }

  return (
    <div className={cn('loginContainerInner')}>
      <Box flx="col" ai="center">
        {shouldResetPassword && (
          <ResetPasswordRequest
            onBackToLoginClick={() => {
              setShouldResetPassword(false);
              setShouldShowLoginPage(true);
            }}
          />
        )}

        {shouldShow2FA && (
          <TwoFactorAuthenticationContainer
            handleContinue={Continue2Fa}
            handleCancel={Cancel2Fa}
            loginRequest={loginRequest}
          />
        )}

        {shouldShowLoginPage && (
          <Box flx="col">
            <Formik
              initialValues={{
                username: '',
                password: '',
              }}
              onSubmit={handlePartnerLogin}
            >
              {({ values, handleChange, handleBlur, handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Box mb="sm" flx="col" height="auto">
                    <div className={cn('fieldLabel')}>{t('Form.usernameLabel', { defaultValue: 'Username' })}</div>

                    <TextField
                      key="username"
                      name="username"
                      placeholder={t('Form.usernamePlaceholder', { defaultValue: 'Username' })}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.username}
                      variant="outlineShadow"
                      elementLeft={<Icon icon="user" size="md" />}
                    />
                  </Box>

                  <Box mb="md" flx="col" height="auto">
                    <div className={cn('fieldLabel')}>{t('Form.passwordLabel', { defaultValue: 'Password' })}</div>

                    <TextField
                      key="password"
                      name="password"
                      placeholder={t('Form.passwordPlaceholder', { defaultValue: 'Password' })}
                      preset="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.password}
                      variant="outlineShadow"
                      elementLeft={<Icon icon="lock" size="md" />}
                    />
                  </Box>

                  <Box className={cn('forgotPasswordRow')} jc="fe" height="auto">
                    <UtilButton
                      id="forgot-password"
                      data-testid="forgot-password"
                      size="large"
                      onClick={() => {
                        setShouldResetPassword(true);
                        setShouldShowLoginPage(false);
                      }}
                    >
                      {t('Form.forgotPasswordButton', { defaultValue: 'Forgot your password?' })}
                    </UtilButton>
                  </Box>

                  <Box jc="fe" flx="col" height="auto">
                    <Button
                      id="login-submit"
                      type="submit"
                      colorScheme="primary"
                      size="large"
                      className={cn('submitButton')}
                      disabled={isSubmitting}
                    >
                      {t('Form.submitButton', { defaultValue: 'Login' })}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>

            {(showMicrosoftSso || showGoogleSso) && (
              <div className={cn('or')}>
                <hr />
                <Title level="6">{t('SSO.orDivider', { defaultValue: 'or' })}</Title>
                <hr />
              </div>
            )}

            {showMicrosoftSso && <MSEntraButton onOpen={setUsingMsEntra} onLogin={handleMsLogin} />}
            {showGoogleSso && <GoogleButton onLogin={handleGoogleLogin} />}

            <Box mt="md" height="auto">
              <p>
                {t('Form.helpText', { defaultValue: 'Need help logging in? Contact' })}{' '}
                <a id="login-support-link" href="mailto:support@vopay.com">
                  support@vopay.com
                </a>
              </p>
            </Box>
          </Box>
        )}
      </Box>

      {usingMsEntra && (
        <div className={cn('usingMsEntra')}>
          <BodyText weight="em">
            {t('SSO.usingMicrosoft', {
              defaultValue: 'You are currently logging in with Microsoft in another window.',
            })}
          </BodyText>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
